import { useState, useEffect } from 'react';
import { 
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Backdrop,
  Grid,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { MatcherGroups, Matchers, Companies } from '../../api/api';
import { KeyTypes, StringOperatorTypes, MatcherOperator, MatcherType } from '../../app/enums';
import { RulesType, defaultRulesType } from '../../models/matcher.interface';
import { Company } from '../../models/location.interface';
import { isEmpty } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    dialogContent: {
      overflow: 'hidden',
    },
    marginBottom: {
      marginBottom: theme.spacing(1),
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#ffffff',
    },
  }),
);

interface IAddToCartDialog {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  loadRules: () => void;
}

interface RuleError {
  make: string
  makeOperator: string
  model: string
  modelOperator: string
  keyType: string
  minYear: string
  maxYear: string
  price: string
  company: string
}

const defaultRuleError: RuleError = {
  make: '',
  makeOperator: '',
  model: '',
  modelOperator: '',
  keyType: '',
  minYear: '',
  maxYear: '',
  price: '',
  company: ''
}

export const AddRuleDialog = ({ open, onClose, onSuccess, loadRules}: IAddToCartDialog) => {
  const classes = useStyles();
  const [ loading, setLoading ] = useState<boolean>(false);
  const [ newRule, setNewRule ] = useState<RulesType>({...defaultRulesType});
  const [ ruleError, setRuleError ] = useState<RuleError>({...defaultRuleError});
  const [ error, setError ] = useState<string>('');
  const [ companies, setCompanies ] = useState<Company[]>([]);

  useEffect(() => {
    Companies.getAllCompanies()
    .then((comps) => { 
      setCompanies(comps.sort((a,b) => (a.name > b.name) ? 1 : -1));
    }).catch((err) => {
      setError(err.response.data.message);
    })
  },[]);

  const handleClose = () => {
    setError('');
    setRuleError(defaultRuleError);
    setNewRule(defaultRulesType);
    onClose();
  }

  const handleChange = (e: React.FormEvent<EventTarget>) => {
    let target = e.target as HTMLInputElement;

    setNewRule({...newRule, [target.id]: target.value});
    setRuleError({...ruleError, [target.id]: ''});
  }

  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;

    setNewRule({...newRule, company: value });
  };

  const validateField = (e: React.FormEvent<EventTarget>) => {
    let target = e.target as HTMLInputElement
    
    if(target.value === undefined || target.value.length < 1)
      setRuleError({...ruleError, [target.id]: 'Required'});
    else
      setRuleError({...ruleError, [target.id]: ''});
  }

  const validatePrice = () => {
    if(newRule.price < 0) {
      setRuleError({...ruleError, price: 'Please enter valid price'})
    } else {
      setRuleError({...ruleError, price: ''})
    }
  } 

  const validateYear = (e: React.FormEvent<EventTarget>) => {
    let target = e.target as HTMLInputElement

    if(+target.value < 0){
      setRuleError({...ruleError, [target.id]: 'Please enter valid year'})
    }else{
      setRuleError({...ruleError, [target.id]: ''})
    }
  }

  const isAddRuleFormValid = () => {
    let success:boolean = true;    
    validatePrice();
    
    if(newRule.make?.length === 0 && newRule.model?.length === 0){
      setRuleError({...ruleError, model: 'Required'})
      setRuleError({...ruleError, make: 'Required'})
      success = false;
    } 
   
    if(newRule.minYear && newRule.maxYear
      && newRule.minYear > newRule.maxYear)
    {
      setRuleError({...ruleError, minYear: 'Please enter valid year'})
      setRuleError({...ruleError, maxYear: 'Please enter valid year'})

      success = false;
    }

    if(!isEmpty(ruleError.make) || !isEmpty(ruleError.model) ||
      !isEmpty(ruleError.minYear) || !isEmpty(ruleError.maxYear) ||
      !isEmpty(ruleError.price)){
        success = false;
    }

    return success;
  }

  const handleSubmit = () => {
    setLoading(true); 

    if(isAddRuleFormValid()) {
      MatcherGroups.createMatcherGroup(true, newRule.keyType, newRule.price)
      .then((matchGroup) => {
        let matcherPromises = [];

        if(newRule.make)
          matcherPromises.push(
            Matchers.createMatcher(
              matchGroup.id,
              MatcherType.MAKE,
              (newRule.makeOperator || MatcherOperator.EQUALS), 
              newRule.make)
          );

        if(newRule.model)
          matcherPromises.push(
            Matchers.createMatcher(
              matchGroup.id,
              MatcherType.MODEL,
              (newRule.modelOperator || MatcherOperator.EQUALS),
              newRule.model)
          );

        if(newRule.minYear)
          matcherPromises.push(
            Matchers.createMatcher(
              matchGroup.id,
              MatcherType.YEAR,
              MatcherOperator.GREATER_THAN_OR_EQUALS,
              newRule.minYear)
          );

        if(newRule.maxYear)
          matcherPromises.push(
            Matchers.createMatcher(
              matchGroup.id, 
              MatcherType.YEAR,
              MatcherOperator.LESS_THAN_OR_EQUALS,
              newRule.maxYear)
          );

        if(newRule.company && newRule.company !== '-1')
          matcherPromises.push(
            Matchers.createMatcher(
              matchGroup.id,
              MatcherType.COMPANY,
              MatcherOperator.EQUALS,
              newRule.company)
          );

        Promise.all(matcherPromises)
          .then((matchers) => {
            setLoading(false);
            onSuccess();
            loadRules();
            handleClose();
          })
          .catch((e) => {
            setError(e.response.data.message);
          });
      }).catch((err) => {
        setError(err.response.data.message);
      }).finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }

  const handleSelectOperatorChange =  (event: React.ChangeEvent<{ value: unknown }>, fieldName: string) => {
    const value:string = event.target.value as string;

    if(fieldName === 'keyType'){
      if(value === 'UNSUPPORTED'){
        setNewRule({
          ...newRule, 
          price: 0,
          keyType: value,
        });
      }else{
        setNewRule({...newRule, [fieldName]: value});
      }
     
      setRuleError({...ruleError, price: ''})
    }else{
      setNewRule({...newRule, [fieldName]: value});
    }
  };

  return ( 
    <Dialog 
      onClose={onClose}
      aria-label="addRuleDialog"
      open={open}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle aria-label="addRuleDialogTitle">
        Add Rule
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {error.length > 0 
          &&
        <Alert
          aria-label="errorAlert"
          severity="error"
          onClose={() => setError('')}
          className={classes.marginBottom}
        >
          {error}
        </Alert>   
        } 
        <Backdrop 
          aria-label="loadingBackdrop"
          className={classes.backdrop}
          open={loading}
        >
          <CircularProgress color="primary" />
        </Backdrop>
        <Grid 
          container
          justifyContent="space-between"
          spacing={1}
        >
          <Grid item xs={6}>
            <TextField
              id="make"
              aria-label="make"
              label="Make"
              margin="normal"
              variant="outlined"
              fullWidth
              onChange={handleChange} 
              error={ruleError.make.length > 0}
              helperText={ruleError.make}
              value={newRule.make}
              inputProps={{ "data-testid": "makeInput" }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Make Operator"
              id="makeOperator"
              aria-label="makeOperator"
              margin="normal"
              variant="outlined"
              fullWidth
              value={newRule.makeOperator}
              onChange={(e) => handleSelectOperatorChange(e,'makeOperator')}   
              disabled={isEmpty(newRule.make)}
              inputProps={{ "data-testid": "makeOperatorInput" }}
              select
            >
              {Object.keys(StringOperatorTypes).map((operatorType) => (
                  <MenuItem key={operatorType} value={operatorType}>
                    {StringOperatorTypes[operatorType as keyof typeof StringOperatorTypes]}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent="space-between"
          spacing={1}
        >
          <Grid item xs={6}>
            <TextField
              id="model"
              aria-label="model"
              label="Model"
              margin="normal"
              variant="outlined"
              fullWidth
              onChange={handleChange} 
              error={ruleError.model.length > 0}
              helperText={ruleError.model}
              inputProps={{ "data-testid": "modelInput" }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Model Operator"
              id="modelOperator"
              aria-label="modelOperator"
              margin="normal"
              variant="outlined"
              fullWidth
              value={newRule.modelOperator}
              onChange={(e) => handleSelectOperatorChange(e,'modelOperator')}   
              inputProps={{ "data-testid": "modelOperatorInput" }}
              disabled={isEmpty(newRule.model)}
              select
            >
              {Object.keys(StringOperatorTypes).map((operatorType) => (
                <MenuItem key={operatorType} value={operatorType}>
                  {StringOperatorTypes[operatorType as keyof typeof StringOperatorTypes]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Grid 
          container
          justifyContent="space-between"
          spacing={1}
        >
          <Grid item xs={6}>
            <TextField
              id="minYear"
              aria-label="minYear"
              inputProps={{ "data-testid": "minYearInput" }}
              label="Min Year"
              fullWidth
              margin="normal"
              variant="outlined"
              type="number"
              onChange={handleChange}
              error={ruleError.minYear.length > 0}
              helperText={ruleError.minYear}
              onBlur={validateYear}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="maxYear"
              aria-label="maxYear"
              inputProps={{ "data-testid": "maxYearInput" }}
              label="Max Year"
              fullWidth
              margin="normal"
              variant="outlined"
              type="number"
              onChange={handleChange}
              error={ruleError.maxYear.length > 0}
              helperText={ruleError.maxYear}
              onBlur={validateYear}
            />
          </Grid>
        </Grid>
        <Grid container>
          <TextField
            id="keyTypes"
            aria-label="keyTypes"
            label="Key Types"
            inputProps={{ "data-testid": "keyTypesInput" }}
            fullWidth
            margin="normal"
            variant="outlined"
            select
            required
            onChange={(e) => handleSelectOperatorChange(e,'keyType')}  
            onBlur={validateField}
            defaultValue="UNSUPPORTED"
          >
            {Object.keys(KeyTypes).map((keyType) => (
              <MenuItem key={keyType} value={keyType}>
                {KeyTypes[keyType as keyof typeof KeyTypes]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid container>
          <TextField
            id="price"
            aria-label="price"
            label="Price"
            fullWidth
            margin="normal"
            variant="outlined"
            disabled={newRule.keyType === 'UNSUPPORTED'}
            type="number"
            value={newRule.price}
            onChange={handleChange}
            onBlur={validatePrice}
            error={ruleError.price.length > 0}
            helperText={ruleError.price}
            required
            inputProps={{ "data-testid": "priceInput" }}
            InputProps={{
                startAdornment:
              <InputAdornment position="start">
                $
              </InputAdornment>,
            }}
          />
        </Grid>
        <Grid container>
          <TextField
            id="company"
            aria-label="company"
            label="Company"
            fullWidth
            margin="normal"
            variant="outlined"
            select
            onChange={handleCompanyChange} 
            defaultValue="-1"
            value={newRule.company || '-1'}
            inputProps={{ "data-testid": "companyList" }}
          >
            <MenuItem key="any" value="-1">
                Any
            </MenuItem>
            {companies.map((comp) => (
              <MenuItem key={comp.name} value={comp.name}>
                {comp.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button 
          aria-label="ruleDialogCancel"
          variant="outlined"
          color="primary"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          aria-label="ruleDialogSubmit"
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddRuleDialog;
